import { usePreAggFilters } from "../hooks/usePreAggFilters";
import { isOwnDuplicateName, parentSourceId } from "../utils/duplicationUtils";
import { normalizeDataType } from "../utils/expressionEditorUtils";
import { deriveFilterMaps } from "../utils/filterSpecUtils";
import DataTypeIcon from "./DataTypeIcon";
import PreAggFilterPopover from "./PreAggFilterPopover";
import { FilterAlt as FilterAltIcon } from "@mui/icons-material";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Button from "components/common/Button";
import * as labels from "config.labels";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as staticDataHooks from "store/hooks/staticDataHooks";

// A stable empty list: a fresh [] each render would re-run the summary effect.
const NO_JOINS = [];

// Names a duplication of parent and lets pre-aggregation filters be chosen
// for it, column by column, before it is created.
const DuplicateNameDialog = ({
  open,
  parent,
  entry,
  columns,
  isNameAvailable,
  onCancel,
  onSave,
  runImmediateReport,
  wrapReport,
  dateFormat,
}) => {
  const translateLabels = staticDataHooks.useTranslator("uiLabels");
  const [name, setName] = useState("");

  const sourceTables = useMemo(() => (parent ? [parentSourceId(parent)] : []), [
    parent,
  ]);
  const outputColumns = useMemo(
    () =>
      parent
        ? columns.map((c) => ({
            column: c.internalName,
            sourceTable: parentSourceId(parent),
          }))
        : [],
    [columns, parent]
  );
  const columnsByName = useMemo(
    () => new Map(columns.map((c) => [c.internalName, c])),
    [columns]
  );
  const getColDef = useCallback(
    (colName) => columnsByName.get(colName) ?? null,
    [columnsByName]
  );

  const {
    preAggFilterCols,
    openFilterPanel,
    buildPreAggFilters,
    setFilterMaps,
    panelProps,
  } = usePreAggFilters({
    sourceTables,
    joinConfigs: NO_JOINS,
    outputColumns,
    getColDef,
    runImmediateReport,
    wrapReport,
    dateFormat,
  });

  // The dialog stays mounted while closed, so each opening starts from the
  // duplication being edited, or clean when creating one; filters chosen and
  // then cancelled never reach the next duplication.
  useEffect(() => {
    if (open) {
      setName(entry?.displayName ?? "");
      setFilterMaps(
        deriveFilterMaps(entry?.specification?.preAggregationFilters)
      );
    }
  }, [open, entry, setFilterMaps]);

  const trimmed = name.trim();
  const available =
    trimmed.length > 0 &&
    (isOwnDuplicateName(name, entry) || isNameAvailable(name));
  const showTaken = trimmed.length > 0 && !available;

  const handleSave = () => {
    if (!available) return;
    onSave(name, buildPreAggFilters());
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth={"sm"} fullWidth>
      <DialogTitle>
        {entry
          ? translateLabels(labels.REPORT_BUILDER_DUPLICATE_EDIT_TITLE, {
              default: "Edit {name}",
              replacements: { name: entry.displayName },
            })
          : translateLabels(labels.REPORT_BUILDER_DUPLICATE_TITLE, {
              default: "Duplicate {parent}",
              replacements: { parent: parent?.displayName ?? "" },
            })}
      </DialogTitle>
      <DialogContent sx={{ flex: "none", overflowY: "visible", pb: 0 }}>
        <TextField
          autoFocus
          margin={"dense"}
          label={translateLabels(labels.REPORT_BUILDER_DUPLICATE_NAME, {
            default: "Duplicate name",
          })}
          fullWidth
          variant={"outlined"}
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={showTaken}
          helperText={
            showTaken
              ? translateLabels(labels.REPORT_BUILDER_DUPLICATE_NAME_TAKEN, {
                  default: "Name already in use",
                })
              : " "
          }
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSave();
            } else if (e.key === "Escape") {
              onCancel();
            }
          }}
        />
      </DialogContent>
      <DialogContent sx={{ pt: 0 }} data-testid={"duplicate-columns-content"}>
        <Typography
          variant={"caption"}
          color={"text.secondary"}
          sx={{ fontWeight: 600, letterSpacing: 0.5 }}
        >
          {translateLabels(labels.REPORT_BUILDER_DUPLICATE_COLUMNS, {
            default: "Columns",
          })}
        </Typography>
        <Table size={"small"}>
          <TableBody>
            {columns.map((col) => {
              const hasFilter = preAggFilterCols.has(col.internalName);
              return (
                <TableRow key={col.internalName}>
                  <TableCell sx={{ width: 24, pr: 0 }}>
                    {col.dataType ? (
                      <DataTypeIcon type={normalizeDataType(col.dataType)} />
                    ) : null}
                  </TableCell>
                  <TableCell>{col.displayName ?? col.internalName}</TableCell>
                  <TableCell align={"right"} sx={{ width: 40 }}>
                    <Tooltip
                      title={translateLabels(
                        labels.REPORT_BUILDER_DUPLICATE_FILTER_TOOLTIP,
                        { default: "Filter rows (before grouping)" }
                      )}
                    >
                      <IconButton
                        size={"small"}
                        onClick={(e) => {
                          e.stopPropagation();
                          openFilterPanel(col.internalName, e.currentTarget);
                        }}
                        sx={{
                          color: hasFilter ? "primary.main" : "action.disabled",
                        }}
                      >
                        <FilterAltIcon fontSize={"small"} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <PreAggFilterPopover panelProps={panelProps} />
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          px: 3,
          pb: 2,
        }}
      >
        <Button onClick={onCancel} color={"secondary"}>
          {translateLabels(labels.REPORT_BUILDER_DUPLICATE_CANCEL, {
            default: "Cancel",
          })}
        </Button>
        <Button onClick={handleSave} isDisabled={!available}>
          {translateLabels(labels.REPORT_BUILDER_DUPLICATE_SAVE, {
            default: "Save",
          })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateNameDialog;
